import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Keyboard, Eye, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { timeAgo } from "@/lib/format";
import { ROLE_LABELS } from "@/lib/constants";

export const NotificationBell = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], unread: 0 });
  const load = () => api.get("/notifications").then(({ data: d }) => setData(d)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 120000); return () => clearInterval(t); }, []);
  const onOpen = (o) => { if (o && data.unread) api.post("/notifications/seen").then(() => setData((d) => ({ ...d, unread: 0 }))).catch(() => {}); };
  return (
    <Popover onOpenChange={onOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" aria-label={data.unread ? `Notifications, ${data.unread} unread` : "Notifications"} data-testid="notification-bell">
          <Bell aria-hidden="true" />
          {data.unread > 0 && <span className="absolute right-1.5 top-1.5 grid h-4 min-w-4 place-items-center rounded-full bg-action px-1 text-[10px] font-semibold text-action-foreground" data-testid="notification-count">{data.unread}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 rounded-xl p-2" data-testid="notification-panel">
        <p className="px-2 py-1.5 text-sm font-semibold">Notifications</p>
        {data.items.length === 0 ? <p className="px-2 py-3 text-sm text-muted-foreground">Nothing new. Announcements and replies to your feedback appear here.</p> : (
          <ul>{data.items.map((n) => (
            <li key={`${n.kind}-${n.id}`}>
              <button type="button" onClick={() => navigate(n.link)} className="flex w-full items-start gap-2 rounded-lg px-2 py-2 text-left text-sm transition-quick hover:bg-muted" data-testid={`notification-${n.id}`}>
                {n.unread && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-action" aria-label="Unread" />}
                <span className="min-w-0"><span className="block truncate font-medium">{n.title}</span><span className="text-xs text-muted-foreground">{n.kind === "reply" ? "Feedback reply" : "Announcement"} · {timeAgo(n.at)}</span></span>
              </button>
            </li>
          ))}</ul>
        )}
      </PopoverContent>
    </Popover>
  );
};

const SHORTCUTS = [["Ctrl or Cmd + K", "Open the command palette"], ["?", "Show this sheet"], ["Up and down arrows", "Move through palette results"], ["Enter", "Open the selected result"], ["Esc", "Close a dialog or palette"], ["Tab", "Move to the next control"]];

export const ShortcutsSheet = () => {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      if (e.key !== "?" || t.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName)) return;
      e.preventDefault();
      setOpen(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="rounded-2xl" data-testid="shortcuts-sheet">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Keyboard className="!h-5 !w-5" aria-hidden="true" /> Keyboard shortcuts</DialogTitle><DialogDescription>Everything in the hub also works with the keyboard alone.</DialogDescription></DialogHeader>
        <dl className="divide-y">{SHORTCUTS.map(([k, d]) => <div key={k} className="flex items-center justify-between gap-4 py-2.5 text-sm"><dt className="text-muted-foreground">{d}</dt><dd><kbd className="rounded-md border bg-muted px-2 py-0.5 font-mono text-xs">{k}</kbd></dd></div>)}</dl>
      </DialogContent>
    </Dialog>
  );
};

export const ViewAsBanner = () => {
  const { viewAs, setViewAs } = useAuth();
  if (!viewAs) return null;
  return (
    <div className="flex items-center justify-center gap-3 bg-action px-4 py-2 text-sm font-medium text-action-foreground" role="status" data-testid="view-as-banner">
      <Eye className="!h-4 !w-4" aria-hidden="true" /> Read-only preview: you are seeing the hub as a {ROLE_LABELS[viewAs]}.
      <button type="button" onClick={() => setViewAs(null)} className="inline-flex items-center gap-1 rounded-lg border border-current px-2 py-0.5" data-testid="view-as-exit"><X className="!h-3.5 !w-3.5" aria-hidden="true" /> Leave preview</button>
    </div>
  );
};
